import React, {
  useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import {
  Box, Button, Checkbox, IconButton, MenuItem, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  UploadFileOutlined as UploadIcon,
  CheckCircle as DoneIcon,
  RemoveCircleOutline as NaIcon,
  ChatBubbleOutlineOutlined as MessageIcon,
  PersonOutline as ThemIcon,
  ScienceOutlined as LabIcon,
  BusinessOutlined as VendorIcon,
} from '@mui/icons-material';
import ScheduleCall from './ScheduleCall';
import type {
  Checkpoint, CheckpointStatus, CandidateStage, BlockedReason, OwedBy,
} from '../../api/services/hiring.service';
import { STAGE_ORDER, TRACK_ORDER } from '../../api/services/hiring.service';
import { alpha } from '@mui/material/styles';
import { status as statusTokens } from '../../theme';
import { transitions } from '../../motion';
import {
  MOVE_LABEL, TRACK_LABEL, TRACK_HINT, checkpointLabel, moveFromOwedBy, moveColor,
  formatDate, stageLabel, dueWords, STAGE_COLOR, withWhom,
} from './hiringUI';

// The checklist.
//
// Two rounds of this were wrong, and the second failure is worth keeping
// written down because it was self-inflicted.
//
//  1. It collapsed every stage but the current one, which hid the done
//     work — half of what a checklist is for.
//  2. Fixing that, every row grew a "Your move" / "Waiting on them" label
//     AND an N/A button AND a control. "Your move" appeared ten times down
//     one column and "N/A" twelve times, which is exactly the repetition
//     the design notes warn against: if a word appears on every row,
//     delete it from every row.
//
// So there are now two things on a row and no more: ONE marker on the
// left carrying both state and whose move it is, and ONE control on the
// right. The words live in the marker's tooltip and in the one-line tally
// above the list. Undo appears on hover, because it is rare. Marking a
// step N/A was dropped: it earned a button on every row and was used on
// almost none. Rows already marked that way still render as N/A.

const NOT_STARTED = '__not_started__';
const IN_PROGRESS = '__in_progress__';
const DONE = '__done__';

/** One width for the control column, so the column reads as a column. */
const CONTROL_WIDTH = 188;

/** The marker gutter. Names and controls line up against it. */
const MARKER_W = 22;
/** The narrowest a track lane can be and still hold its name and a
 *  readable control under it (lane padding, the marker indent, the
 *  120px select floor, the message button). */
const LANE_MIN = 240;
const LANE_GAP = 20;
/** Below this the card cannot fit a name AND a 188px control on one
 *  line, so every row stacks the way a lane row does. */
const ROW_MIN = 480;

/**
 * State and ownership in a single mark.
 *
 * These used to be two separate things — an icon for the state and a line
 * of text for the owner — which meant a column of repeated words next to a
 * column of repeated icons. An outstanding step is a ring, and the ring is
 * blue when it is yours to do.
 */
/**
 * Outstanding is a SQUARE; finished is a circle.
 *
 * The shape carries the state on its own, before any colour is read — a
 * column of squares is work left, and every circle in it is done. Colour
 * then says whose move it is among the squares.
 */
const Ring: React.FC<{ mine?: boolean }> = ({ mine }) => (
  <Box
    sx={{
      width: 15,
      height: 15,
      borderRadius: 0.5,
      border: '2px solid',
      borderColor: mine ? moveColor('you') : 'divider',
      bgcolor: mine ? (theme) => alpha(theme.palette.primary.main, 0.16) : 'transparent',
    }}
  />
);

const StateMark: React.FC<{
  status: CheckpointStatus;
  blocked: boolean;
  isDocument: boolean;
  owedBy?: OwedBy | null;
  isNext?: boolean;
}> = ({ status, isNext }) => {
  if (status === 'done') return <DoneIcon sx={{ fontSize: 19, color: statusTokens.ok }} />;
  if (status === 'not_applicable') return <NaIcon sx={{ fontSize: 18, color: 'text.disabled' }} />;
  // No padlock. Nothing here is locked any more — a dependency greys a
  // row and says what it is waiting for; it does not stop anybody. An icon
  // that means "you cannot" on a control that you can is worse than no
  // icon at all.

  // Solid rings, never dashed. A dashed outline reads as a placeholder
  // for a control rather than as a state, and a column of them looks like
  // the page failed to finish loading.
  // A row that is waiting on something else is NOT your move, whatever
  // its owner says — blue on a greyed row is the screen contradicting
  // itself in the same glance.
  // ONE blue per checklist: the step the to-do engine says comes next.
  // Every whose-move-is-it ring painted itself blue before, so Setup lit
  // up wall to wall while E-Verify — the actual next thing — sat plain.
  // Part-way wears the amber, and only the MARKER wears it: an orange
  // wash across the whole row shouts about a step that is simply in
  // progress, which is where most candidates sit most of the time. Same
  // ring-and-dot the grid uses, so the two screens say it the same way.
  if (status === 'in_progress') {
    return (
      <Box
        sx={{
          width: 15, height: 15, borderRadius: 0.5,
          border: '2px solid', borderColor: statusTokens.warn,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {/* 15 outside, 2 border, 5 dot: the gaps divide evenly, so the dot
            sits dead centre rather than a sub-pixel off it. */}
        <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: statusTokens.warn }} />
      </Box>
    );
  }

  const mine = !!isNext;
  return (
    <Box
      sx={{
        width: 15,
        height: 15,
        borderRadius: '50%',
        border: '2px solid',
        borderColor: mine ? moveColor('you') : 'divider',
        // `theme.palette.primary.main`, not `moveColor()`. The status
        // tokens are CSS custom properties so they can flip with the
        // theme, and `alpha()` parses a colour string — handed
        // `var(--topo-status-info)` it throws and takes the whole page
        // down with it. The palette holds a real hex in both modes.
        bgcolor: mine
          ? (theme) => alpha(theme.palette.primary.main, 0.16)
          : 'transparent',
      }}
    />
  );
};

const CheckpointRow: React.FC<{
  checkpoint: Checkpoint;
  disabled: boolean;
  blockedBy?: { steps: string[]; from: string | null; note: string | null };
  /** In a track column there is no room beside the label, so the control
   *  sits under it. Same row, two lines. */
  stacked?: boolean;
  onSet: (
    status: CheckpointStatus,
    opts?: { value?: string; state?: string | null; at?: { x: number; y: number } },
  ) => void;
  /** The candidate's current value, for a field-backed row. */
  fieldValue?: string;
  /** The one step the to-do engine says comes next — the only blue. */
  isNext?: boolean;
  /** Field-backed rows write straight to the candidate record. */
  onSetField: (field: string, value: string) => void;
  /** Unticking a filed document means removing the file. */
  onRemoveFile: () => void;
  onUpload: () => void;
  /** The message this step is the doing of, ready to copy. */
  message?: { label: string; body: string } | null;
  onCopyMessage?: () => void;
}> = ({
  checkpoint, disabled, blockedBy, stacked, fieldValue, isNext,
  onSet, onSetField, onRemoveFile, onUpload, message, onCopyMessage,
}) => {
  const [value, setValue] = useState(checkpoint.value || '');
  useEffect(() => { setValue(checkpoint.value || ''); }, [checkpoint.value]);

  const done = checkpoint.status === 'done';
  const na = checkpoint.status === 'not_applicable';
  const blocked = !done && !na && !!blockedBy;
  const isDocument = checkpoint.kind === 'document';
  const states = checkpoint.states || [];
  const hasStates = states.length > 0;
  // Blocked greys the row; it does not disable it. A dependency is
  // advice — the world does things out of order, and a control somebody
  // cannot reach is how a system earns a paper workaround.
  const locked = disabled;

  // The one task, in one of its states. "Background check link sent" and
  // "they completed the form" were two rows for one errand; this is that
  // errand, and the dropdown is where it has got to.
  // Part-way with no NAMED state (an import's "Processing") must not
  // fall back to "Not started" — the same lie the grid told once.
  const current = done ? DONE
    : checkpoint.status === 'in_progress' ? (checkpoint.state || IN_PROGRESS)
      : NOT_STARTED;

  // Nothing to progress through and nothing to upload: a tick is the whole
  // interaction, and it belongs in the marker rather than in a control.
  // `!blocked` used to be in here too, so a blocked manual step rendered
  // a padlock with nothing to click. Blocked greys a row; it does not take
  // its control away.
  // A valueOptional value row ticks like a manual one — the pass is the
  // tick, the date is a bonus typed beside it. "Checkbox first, then the
  // date passed field", per the owner, on one line.
  const tickableValue = checkpoint.kind === 'value' && !!checkpoint.valueOptional && !hasStates;
  const simpleManual = (checkpoint.kind === 'manual' || tickableValue) && !hasStates && !done && !na;

  // A field-backed row is satisfied by the candidate record, so it never
  // offers a way to type a second copy — it says where the answer lives.
  const isField = checkpoint.kind === 'field';

  // A date wants a date control. The safety quiz's "Date passed" was a
  // free-text box, which is both harder to fill and the input to the
  // +3-day road-test rule — a typo there moves a real appointment.
  const isDate = checkpoint.valueType === 'date';

  const tip = done
    ? (checkpoint.kind === 'field'
      ? `On the record${fieldValue ? `: ${fieldValue}` : ''}`
      : `Done${checkpoint.completedAt ? ` · ${formatDate(checkpoint.completedAt)}` : ''}`)
    : na ? 'Does not apply to this person'
      : blocked
        ? (blockedBy!.steps.length
          ? `Needs ${blockedBy!.steps.join(' and ')} first`
          : `Not until ${formatDate(blockedBy!.from)}${blockedBy!.note ? ` — ${blockedBy!.note}` : ''}`)
        : checkpoint.kind === 'field'
          ? 'Ticks itself once this is on the record'
          : [
            MOVE_LABEL[moveFromOwedBy(checkpoint.owedByNow) || 'you'],
            dueWords(checkpoint),
          ].filter(Boolean).join(' · ');

  /**
   * Which single control this row gets.
   *
   * Written as one decision rather than a stack of conditions, because the
   * stack was what let the safety quiz render a text box AND a dropdown
   * side by side — two controls saying different halves of one thing.
   */
  const control: 'none' | 'states' | 'upload' | 'value' | 'when' = (() => {
    if (disabled) return 'none';
    // ALWAYS the dropdown once a checkpoint has states, including when it
    // is finished, so a wrong pick can be taken back.
    if (hasStates) return 'states';
    // The ONE field-backed row that keeps a control. A time somebody
    // agreed to is not typed into Details on the way past — it is said on
    // a call and written down there and then, and the row is where you
    // are looking when they say it.
    if (checkpoint.valueType === 'datetime' && checkpoint.candidateField) return 'when';
    // NO control for a field-backed row, not even the input it used to
    // carry ("i dont like this do it in details thing. remove the field
    // next to it"): the record's own field is the single place the
    // answer is typed, the server ticks this row the moment it is
    // non-empty, and empties it back. The row is a mirror, and a mirror
    // with a keyboard is two copies again.
    if (checkpoint.kind === 'field') return 'none';
    // A pass WITH A DATE keeps its date after the tick. The quiz is the
    // case: the date is the record — the road test counts three days
    // from it — and hiding the box the moment the tick landed made the
    // one thing somebody had written down disappear. It is still
    // editable, because a date typed from memory is a date sometimes
    // typed wrong.
    if (checkpoint.kind === 'value' && checkpoint.valueOptional && !na) return 'value';
    if (done || na) return 'none';
    if (isDocument) return 'upload';
    if (checkpoint.kind === 'value') return 'value';
    return 'none';
  })();

  // A value checkpoint that also has states needs both: the dropdown says
  // where it has got to, the box holds the answer. The safety quiz has a
  // date to record AND a GroundCloud invite to track, and collapsing that
  // to one control is why its date could not be entered at all.
  const alsoValue = control === 'states' && checkpoint.kind === 'value';

  /**
   * One control shape everywhere: same width, same font, same height.
   * In a lane it runs the full width under the name; across the full
   * width it is a fixed box beside it.
   */
  const CONTROL_SX = {
    // `flex: 1` in a lane, not `width: 100%`. The message button moved to
    // sit BESIDE the control, and a control claiming the full width of
    // the row pushed that button out of the lane — which cropped the
    // "Received / not yet" box against the card's edge.
    // A floor as well as a share. Below about 120px a select renders as
    // its own caret with the word behind it, which is how "Link sent"
    // became "▾ Link" sitting on top of the box.
    ...(stacked ? { flex: 1, minWidth: 120 } : { width: CONTROL_WIDTH }),
    '& .MuiInputBase-input': { fontSize: 13, py: 0.75, pl: 1.25 },
    '& .MuiSelect-select': { pr: '30px !important' },
  } as const;

  /** Clicking the tick undoes it — quicker than hunting a hover button.
   *  Field-backed rows are mirrors of the record and untick only when
   *  the field empties, so the hand stays off them. */
  const canUntick = (done || na) && !disabled && !isField;

  /**
   * A document checkpoint is green BECAUSE the file is there, so unticking
   * one is really a question about the file. It used to just set the row
   * back to not-started, and the next sync put it straight back — the
   * screen appeared to accept something it had not done.
   */
  const untick = () => {
    if (isDocument && done) { onRemoveFile(); return; }
    onSet('not_started', { state: null });
  };

  /* ---------------------------------------------------------------
   * A row is three pieces: a marker, a name, and (sometimes) a control.
   * They are defined once here and arranged differently depending on
   * whether the row is in a narrow track lane or across the full width.
   * ------------------------------------------------------------- */

  const marker = (
    <Tooltip
      title={done && isDocument ? `${tip} — click to remove the file`
        : done || na ? `${tip} — click to undo` : tip}
      placement="top"
    >
      <Box
        role={canUntick ? 'button' : undefined}
        tabIndex={canUntick ? 0 : undefined}
        aria-label={canUntick ? `Undo ${checkpointLabel(checkpoint)}` : undefined}
        onClick={canUntick ? untick : undefined}
        onKeyDown={canUntick ? (e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); untick(); }
        } : undefined}
        sx={{
          width: MARKER_W, display: 'flex', justifyContent: 'center', flexShrink: 0,
          cursor: canUntick ? 'pointer' : 'default',
          borderRadius: '50%',
          '&:hover': canUntick ? { opacity: 0.6 } : undefined,
          '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
        }}
      >
        {/* A plain manual step is ticked from the marker itself — a tick
            already says "done"; a 190px "Mark done" button said it again
            and cost a fifth of the row to do it. */}
        {simpleManual ? (
          <Checkbox
            checked={false}
            disabled={disabled}
            size="small"
            // A CIRCLE, like every other marker in the list. MUI's default
            // is a square, which made plain manual steps the only rows
            // shaped differently from the rest.
            icon={<Ring mine={!!isNext} />}
            checkedIcon={<DoneIcon sx={{ fontSize: 19, color: statusTokens.ok }} />}
            sx={{ p: 0.25, ...(blocked && { color: 'text.disabled' }) }}
            inputProps={{ 'aria-label': checkpointLabel(checkpoint) }}
            // Hands up where it was, so anything celebrating can start at
            // the control rather than in the middle of the window.
            onChange={(e) => {
              const r = (e.target as HTMLElement).getBoundingClientRect();
              onSet('done', { value: value || undefined, at: { x: r.left + r.width / 2, y: r.top + r.height / 2 } });
            }}
          />
        ) : (
          <StateMark
            status={checkpoint.status}
            blocked={blocked}
            isDocument={isDocument}
            owedBy={checkpoint.owedByNow}
            isNext={isNext}
          />
        )}
      </Box>
    </Tooltip>
  );

  const party = withWhom({ key: checkpoint.key, owedBy: checkpoint.owedByNow });

  // Ordinary text. Everything that used to sit beside it — the
  // completion date, what a blocked step waits on, where a field-backed
  // value is typed — lives in the marker's tooltip.
  const late = !done && !na && (checkpoint.overdueDays || 0) > 0;
  const withParty = !done && !na && !blocked && !!party;

  // Who the step is with, as a small mark at the end of the name — a
  // person for the candidate, a flask for the lab, a building for First
  // Advantage or FedEx — and red when the step is late. Words only on
  // hover. As a line of text it ran into the name in a narrow lane, and
  // as a subtitle it was still a sentence per row to read past; a mark
  // is there when you look for it and quiet when you don't.
  // Our own late steps get a red dot in the same place. Nothing on a
  // blocked row: nothing is with anybody until it can start.
  const OwnerIcon = !withParty ? null
    : checkpoint.owedByNow === 'them' ? ThemIcon
      : checkpoint.key === 'drug_screen' ? LabIcon
        : VendorIcon;
  const hint = [
    withParty ? party.charAt(0).toUpperCase() + party.slice(1) : '',
    late ? dueWords(checkpoint) : '',
  ].filter(Boolean).join(' · ');
  const hintCased = hint.charAt(0).toUpperCase() + hint.slice(1);

  const name = (
    // In a lane the name takes the row, so the mark sits at its far end;
    // across the full width it hugs the name, and the spacer after the
    // control keeps the control where it was.
    <Stack direction="row" alignItems="flex-start" gap={0.75} sx={{ minWidth: 0, ...(stacked && { flex: 1 }) }}>
      <Typography
        variant="body2"
        color={na ? 'text.disabled' : 'text.primary'}
        sx={{ minWidth: 0, lineHeight: 1.45, ...(stacked && { flex: 1 }) }}
      >
        {checkpointLabel(checkpoint)}
      </Typography>
      {(OwnerIcon || late) && (
        <Tooltip title={hintCased} placement="top">
          <Box
            aria-label={hintCased}
            sx={{
              flexShrink: 0, height: 20, display: 'flex', alignItems: 'center',
              color: late ? statusTokens.danger : 'text.disabled',
            }}
          >
            {OwnerIcon
              ? <OwnerIcon sx={{ fontSize: 16 }} />
              : <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: statusTokens.danger }} />}
          </Box>
        </Tooltip>
      )}
    </Stack>
  );

  /**
   * The control, and the message beside it.
   *
   * A ROW, not a column. The button lived inside the control stack, which
   * is a column so that a states dropdown and its value box can sit one
   * above the other — and the message quietly inherited that, landing
   * under the dropdown where it read as a second control rather than as
   * the text that step is the doing of.
   */
  const controls = (
    <Stack
      direction="row"
      alignItems="center"
      gap={0.5}
      // In a lane the pair fills the row; across the full width it hugs.
      sx={stacked ? { width: '100%', minWidth: 0 } : { flexShrink: 0 }}
    >
    <Stack
      direction="column"
      gap={0.75}
      sx={{
        minWidth: 0,
        ...(stacked ? { flex: 1 } : { flexShrink: 0 }),
        display: control === 'none' ? 'none' : 'flex',
      }}
    >
      {control === 'states' && (
        <TextField
          select
          size="small"
          value={current}
          disabled={locked}
          SelectProps={{
            displayEmpty: true,
            renderValue: () => {
              const word = current === DONE
                ? (checkpoint.doneLabel || 'Done')
                : current === IN_PROGRESS
                  ? 'In progress'
                  : current === NOT_STARTED
                    ? 'Not started'
                    : (states.find((st) => st.key === current)?.label || current);
              return (
                <Box component="span" sx={{ color: current === NOT_STARTED ? 'text.disabled' : 'text.primary' }}>
                  {word}
                </Box>
              );
            },
          }}
          sx={CONTROL_SX}
          onChange={(e) => {
            const picked = e.target.value;
            if (picked === NOT_STARTED) { onSet('not_started', { state: null }); return; }
            if (picked === DONE) {
              if (isDocument) { onUpload(); return; }
              onSet('done', { value });
              return;
            }
            onSet('in_progress', { state: picked });
          }}
        >
          <MenuItem value={NOT_STARTED}>Not started</MenuItem>
          {current === IN_PROGRESS && <MenuItem value={IN_PROGRESS} disabled>In progress</MenuItem>}
          {states.map((st) => (
            <MenuItem key={st.key} value={st.key}>{st.label}</MenuItem>
          ))}
          <MenuItem value={DONE}>
            {isDocument ? 'Upload the file…' : (checkpoint.doneLabel || 'Done')}
          </MenuItem>
        </TextField>
      )}

      {control === 'when' && (
        <ScheduleCall
          scheduledCallAt={fieldValue || null}
          onSave={(iso) => onSetField(checkpoint.candidateField!, iso || '')}
        />
      )}

      {(control === 'value' || alsoValue) && (
        <TextField
          size="small"
          type={isDate ? 'date' : 'text'}
          placeholder={isDate ? undefined : (checkpoint.valueLabel || 'Value')}
          value={value}
          disabled={locked}
          sx={CONTROL_SX}
          onChange={(e) => setValue(e.target.value)}
          onBlur={() => {
            if ((checkpoint.value || '') === value) return;
            // On a valueOptional row the TICK is the pass and the date
            // is written beside it, so correcting the date must not
            // untick the quiz — and typing one on an unticked row still
            // means it happened.
            if (checkpoint.valueOptional) {
              onSet(done || value.trim() ? 'done' : 'not_started', { value });
              return;
            }
            onSet(value.trim() ? 'done' : 'not_started', { value });
          }}
        />
      )}

      {/* One shape for one action. Upload used to be contained when the
          step was yours and outlined when it was not, so two Upload
          buttons three rows apart looked like two different controls. */}
      {control === 'upload' && (
        <Button
          size="small"
          variant="outlined"
          startIcon={<UploadIcon />}
          onClick={onUpload}
          disabled={locked}
          sx={{ ...CONTROL_SX, justifyContent: 'center', fontSize: 13, py: 0.6 }}
        >
          Upload
        </Button>
      )}
    </Stack>
      {/* The message this step IS. "Send the first message" and "Outreach
          sent" are the same thing seen from two directions, so the text
          is offered on the row rather than two clicks away in a dialog.
          On hover, like every other rare affordance here. */}
      {message && (
        <Tooltip title={`Copy the ${message.label.toLowerCase()} message`}>
          <IconButton
            size="small"
            className="row-message"
            data-testid={`copy-message-${checkpoint.key}`}
            onClick={onCopyMessage}
            sx={{
              p: 0.25, flexShrink: 0, color: 'text.disabled', opacity: 0,
              transition: transitions(['opacity']),
              '&:hover': { color: 'primary.main' },
              '&:focus-visible': { opacity: 1 },
            }}
          >
            <MessageIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );

  /**
   * IN A LANE: the name owns its own line and the control sits UNDER it,
   * indented to the name's left edge and running the width of the lane.
   * Nothing is beside anything, so nothing can end up on top of anything
   * — which is what happened every time the name and the control had to
   * share 250px: 'Onboarding packet' disappeared behind its own box.
   * The rows get real air between them instead of being packed.
   */
  if (stacked) {
    return (
      <Box
        sx={{
          px: 1,
          py: 1.25,
          borderRadius: 1.5,
          transition: transitions(['background-color']),
          '&:hover': { bgcolor: 'action.selected', '.row-message': { opacity: 1 } },
        }}
      >
        <Stack direction="row" alignItems="flex-start" gap={1.25} sx={{ minWidth: 0 }}>
          {marker}
          {name}
        </Stack>
        {/* `|| message`: a lane row can have a message and no control —
            the button is not a control's companion, it is the row's. */}
        {(control !== 'none' || message) && (
          <Box sx={{ pl: `${MARKER_W + 10}px`, mt: 1 }}>{controls}</Box>
        )}
      </Box>
    );
  }

  // ACROSS THE FULL WIDTH: there is room for one line, so the control
  // sits right after the name rather than being pushed to a far column.
  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={1.25}
      sx={{
        minHeight: 40,
        py: 0.5,
        pl: 0.75,
        pr: 0.5,
        borderRadius: 1,
        transition: transitions(['background-color']),
        '&:hover': { bgcolor: 'action.hover', '.row-message': { opacity: 1 } },
      }}
    >
      {marker}
      {name}
      {controls}
      {/* Absorbs what is left, so the control stays beside the name. */}
      <Box sx={{ flex: 1, minWidth: 0 }} />
    </Stack>
  );
};

const CheckpointList: React.FC<{
  checkpoints: Checkpoint[];
  currentStage: CandidateStage;
  closed: boolean;
  blocked?: Record<string, BlockedReason>;
  /** The one step the to-do engine says comes next — paints the one blue marker. */
  nextActionKey?: string | null;
  onSet: (
    key: string,
    status: CheckpointStatus,
    opts?: { value?: string; state?: string | null; at?: { x: number; y: number } },
  ) => void;
  /** Writes a field-backed checkpoint's value onto the candidate record. */
  onSetField: (field: string, value: string) => void;
  /** Asks to remove the file behind a document checkpoint. */
  onRemoveFile: (checkpoint: Checkpoint) => void;
  /** Current values for field-backed checkpoints, keyed by candidate field. */
  fieldValues?: Record<string, string | null | undefined>;
  onUpload: (checkpoint: Checkpoint) => void;
  /** Messages that belong beside a step, keyed by that step's key. */
  messages?: Record<string, { label: string; body: string }>;
  onCopyMessage?: (message: { label: string; body: string }) => void;
}> = ({
  checkpoints, currentStage, closed, blocked = {}, fieldValues = {}, nextActionKey = null,
  onSet, onSetField, onRemoveFile, onUpload, messages = {}, onCopyMessage,
}) => {
  /**
   * Is there room for the tracks side by side?
   *
   * Asked of THIS card's width, not the window's. It used to be the `lg`
   * breakpoint, but the card shares the page with the nav, the candidate
   * list and Details, so just past 1200px each lane was ~150px and the
   * dropdowns spilled into the lane beside them. Measured, the lanes
   * appear exactly when they fit, at any window size and whatever else
   * is open beside the card.
   */
  const rootRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;
    setWidth(el.getBoundingClientRect().width);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const lanesFit = (count: number) => width >= count * LANE_MIN + (count - 1) * LANE_GAP;
  const narrow = width > 0 && width < ROW_MIN;

  const byKey = useMemo(() => new Map(checkpoints.map((c) => [c.key, c])), [checkpoints]);

  const blockedLabels = (key: string) => {
    const reason = blocked[key];
    if (!reason) return undefined;
    return {
      steps: (reason.waitingFor || []).map((k) => {
        const definition = byKey.get(k);
        return definition ? checkpointLabel(definition) : k;
      }),
      from: reason.availableFrom,
      note: reason.note,
    };
  };

  const groups = useMemo(() => {
    const extra = Array.from(new Set(
      checkpoints.map((c) => c.stage).filter((s) => !STAGE_ORDER.includes(s)),
    ));
    return [...STAGE_ORDER, ...extra]
      .map((stage) => ({ stage, items: checkpoints.filter((c) => c.stage === stage) }))
      .filter((group) => group.items.length > 0);
  }, [checkpoints]);

  const [hideDone, setHideDone] = useState(false);

  const tally = useMemo(() => {
    let done = 0; let total = 0;
    checkpoints.forEach((c) => {
      if (!c.required) return;
      total += 1;
      if (c.status === 'done' || c.status === 'not_applicable') done += 1;
    });
    return { done, total };
  }, [checkpoints]);

  const pct = tally.total ? Math.round((tally.done / tally.total) * 100) : 0;

  const renderRows = (items: Checkpoint[], stacked = false) => items.map((checkpoint) => (
    <CheckpointRow
      key={checkpoint.key}
      checkpoint={checkpoint}
      disabled={closed}
      stacked={stacked}
      isNext={checkpoint.key === nextActionKey}
      blockedBy={blockedLabels(checkpoint.key)}
      fieldValue={checkpoint.candidateField
        ? (fieldValues[checkpoint.candidateField] ?? '') || ''
        : undefined}
      onSet={(status, opts) => onSet(checkpoint.key, status, opts)}
      onSetField={onSetField}
      onRemoveFile={() => onRemoveFile(checkpoint)}
      onUpload={() => onUpload(checkpoint)}
      message={messages[checkpoint.key] || null}
      onCopyMessage={() => {
        const message = messages[checkpoint.key];
        if (message && onCopyMessage) onCopyMessage(message);
      }}
    />
  ));

  return (
    <Box ref={rootRef}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2} sx={{ mb: 1 }}>
        {/* Just the count. The tally used to carry "5 to action · 1
            awaiting third party · 7 later" beside it, which is three more
            numbers to reconcile against a list that already shows, row by
            row, what is done and which one is next. */}
        <Typography variant="body2" color="text.primary">
          <strong>{tally.done} of {tally.total} done</strong>
        </Typography>
        <Button size="small" color="inherit" onClick={() => setHideDone((v) => !v)}>
          {hideDone ? 'Show done' : 'Hide done'}
        </Button>
      </Stack>

      <Box sx={{ height: 4, borderRadius: 999, bgcolor: 'action.hover', mb: 1.25, overflow: 'hidden' }}>
        <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: statusTokens.ok, transition: transitions(['width']) }} />
      </Box>

      {/* The marks say three things, so the screen should say what they
          are once rather than leave somebody to work it out. */}
      <Stack direction="row" gap={2} flexWrap="wrap" sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" gap={0.75}>
          <Box
            sx={{
              width: 12, height: 12, borderRadius: '50%', border: '2px solid',
              borderColor: moveColor('you'),
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.16),
            }}
          />
          <Typography variant="caption" color="text.secondary">Next action</Typography>
        </Stack>
        <Stack direction="row" alignItems="center" gap={0.75}>
          <Box sx={{ width: 12, height: 12, borderRadius: '50%', border: '2px solid', borderColor: 'divider' }} />
          <Typography variant="caption" color="text.secondary">Still to do</Typography>
        </Stack>
        {/* The middle state, which the legend was missing — and it is
            where most candidates sit for most of the time. Same ring and
            dot as the row itself, at legend size. */}
        <Stack direction="row" alignItems="center" gap={0.75}>
          <Box
            sx={{
              width: 12, height: 12, borderRadius: 0.5, border: '2px solid',
              borderColor: statusTokens.warn,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: statusTokens.warn }} />
          </Box>
          <Typography variant="caption" color="text.secondary">Part-way</Typography>
        </Stack>
        <Stack direction="row" alignItems="center" gap={0.75}>
          <DoneIcon sx={{ fontSize: 14, color: statusTokens.ok }} />
          <Typography variant="caption" color="text.secondary">Complete</Typography>
        </Stack>
        {/* The marks at the end of a row: who a step is sitting with, red
            when it is late. Said once here, so the rows need no words. */}
        <Stack direction="row" alignItems="center" gap={0.75}>
          <ThemIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
          <Typography variant="caption" color="text.secondary">With them</Typography>
        </Stack>
        <Stack direction="row" alignItems="center" gap={0.75}>
          <LabIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
          <Typography variant="caption" color="text.secondary">At the lab</Typography>
        </Stack>
        <Stack direction="row" alignItems="center" gap={0.75}>
          <VendorIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
          <Typography variant="caption" color="text.secondary">With an outside party</Typography>
        </Stack>
        <Stack direction="row" alignItems="center" gap={0.75}>
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: statusTokens.danger }} />
          <Typography variant="caption" color="text.secondary">Late</Typography>
        </Stack>
      </Stack>

      {groups.map(({ stage, items }) => {
        const visible = hideDone
          ? items.filter((c) => c.status !== 'done' && c.status !== 'not_applicable')
          : items;
        if (visible.length === 0) return null;

        const required = items.filter((c) => c.required);
        const finished = required.filter(
          (c) => c.status === 'done' || c.status === 'not_applicable',
        ).length;
        const complete = required.length > 0 && finished === required.length;
        const current = stage === currentStage;
        const tracked = TRACK_ORDER.filter((t) => visible.some((c) => c.track === t));
        const lanes = lanesFit(tracked.length);
        // A finished stage takes on its own colour, faintly — the same
        // stage colours the mobile checklist and the board use — so a
        // glance down the card shows which stages are behind them. One
        // wash for the whole stage, header included; the words keep their
        // usual colour and only the tick carries the hue. Unfinished
        // stays neutral.
        const hue = STAGE_COLOR[stage] || STAGE_COLOR.lead;
        const trackDone = (track: string) => {
          const inTrack = items.filter((c) => c.track === track && c.required);
          return inTrack.length > 0
            && inTrack.every((c) => c.status === 'done' || c.status === 'not_applicable');
        };

        return (
          <Box
            key={stage}
            sx={{
              mb: 2, px: 1.25, pb: 1, borderRadius: 2,
              bgcolor: complete ? alpha(hue, 0.05) : 'transparent',
              transition: transitions(['background-color']),
            }}
          >
            <Stack
              direction="row" alignItems="center" gap={1}
              sx={{
                // In line with the ticks below, not pressed to the edge.
                py: 0.5, mb: 0.5, mx: -1.25, px: 2,
                borderBottom: '1px solid',
                borderColor: complete ? alpha(hue, 0.25) : 'divider',
                transition: transitions(['border-color']),
              }}
            >
              <Typography
                variant="overline"
                sx={{
                  color: current ? 'primary.main' : 'text.disabled',
                  fontWeight: current ? 700 : 500,
                  letterSpacing: '.08em',
                  lineHeight: 1.6,
                }}
              >
                {stageLabel(stage)}
              </Typography>
              {current && <Typography variant="caption" color="primary.main">they are here now</Typography>}
              <Box sx={{ flex: 1 }} />
              <Typography variant="caption" sx={{ color: complete ? 'text.secondary' : 'text.disabled' }}>
                {required.length === 0 ? '—' : `${finished} of ${required.length}`}
              </Typography>
              {complete && <DoneIcon sx={{ fontSize: 14, color: hue }} />}
            </Stack>

            {/* Three tracks run at once inside `clearing`, and nothing in
                one blocks anything in another. Flattened, somebody three
                weeks into a background check looks identical to somebody
                who has not started their safety quiz. */}
            {/* Side by side, because they ARE side by side. Stacked, a
                candidate three weeks into a background check looked
                exactly like one who had not started their safety quiz —
                and the screen implied an order between three things that
                have none. Columns also come out SHORTER than the stack,
                since the height is the tallest track rather than the sum
                of all three. */}
            {tracked.length > 0 ? (
              <>
                <Box
                  sx={{
                    display: 'grid',
                    // Three lanes only when there is ACTUALLY room for
                    // three controls (see `lanesFit`). Otherwise the
                    // tracks stack and every row gets the full width,
                    // which is a worse overview and a usable one.
                    gridTemplateColumns: lanes ? `repeat(${tracked.length}, minmax(0, 1fr))` : '1fr',
                    gap: `${LANE_GAP}px`,
                    // Stretch, not start. Three panels of different heights
                    // next to each other read as three unrelated things;
                    // the whole point is that they run together.
                    alignItems: 'stretch',
                  }}
                >
                  {tracked.map((track) => (
                    <Box
                      key={track}
                      sx={{
                        borderRadius: 2,
                        // A finished track gets the stage's wash on its
                        // own, before the whole stage is done. Once the
                        // stage is done its wash covers everything, so the
                        // lanes turn into plain panels sitting on it —
                        // still grouped, and only one colour on screen.
                        // (Outlined lanes were tried and rejected.)
                        bgcolor: complete ? 'background.paper'
                          : trackDone(track) ? alpha(hue, 0.05) : 'action.hover',
                        transition: transitions(['background-color']),
                        p: 1.75,
                        minWidth: 0,
                        height: '100%',
                      }}
                    >
                      <Tooltip title={TRACK_HINT[track] || ''} placement="top">
                        <Stack direction="row" alignItems="center" gap={0.75} sx={{ pl: 1, pb: 1.25 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'text.secondary', fontWeight: 700,
                              fontSize: 10.5, letterSpacing: '.07em', textTransform: 'uppercase',
                              cursor: 'default',
                            }}
                          >
                            {TRACK_LABEL[track]}
                          </Typography>
                          {trackDone(track) && <DoneIcon sx={{ fontSize: 13, color: hue }} />}
                        </Stack>
                      </Tooltip>
                      {renderRows(visible.filter((c) => c.track === track), lanes || narrow)}
                    </Box>
                  ))}
                </Box>

                {/* Where the three meet. Full width, under all of them. */}
                {visible.some((c) => !c.track) && (
                  <Box sx={{ mt: 1.5 }}>
                    {renderRows(visible.filter((c) => !c.track), narrow)}
                  </Box>
                )}
              </>
            ) : renderRows(visible, narrow)}
          </Box>
        );
      })}
    </Box>
  );
};

export default CheckpointList;
